import React, { useState, useMemo } from 'react';

const tiers = {
    7:  { label: "7D",  mult: 1.0,  desc: "7 Days" },
    14: { label: "14D", mult: 1.5,  desc: "14 Days" },
    30: { label: "30D", mult: 2.5,  desc: "30 Days" },
    90: { label: "90D", mult: 6.0,  desc: "90 Days" }
  };

const PoliticalStaking = ({ userData, currentUser, onClose }) => {
  const [stakeAmount, setStakeAmount] = useState("");
  const [duration, setDuration] = useState(7);
  const [loading, setLoading] = useState(false);

  const THEME_TEAL = "#50e3c2";
  const DEFAULT_GREY = "#333";
  const TEXT_GREY = "#888";

  const POINTS_PER_MILLION_BASE = 10;
  const activeStake = userData?.activeStake;

  const isMatured = useMemo(() => {
    if (!activeStake?.endDate) return false;
    const maturityMs = activeStake.endDate.seconds * 1000;
    return Date.now() >= maturityMs;
  }, [activeStake]);

  const projectedEarnings = useMemo(() => {
    const amt = Number(stakeAmount) || 0;
    const millions = amt / 1000000;
    const daily = millions * POINTS_PER_MILLION_BASE * tiers[duration].mult;
    return { daily, total: daily * duration };
  }, [stakeAmount, duration]);

  const handleStake = async () => {
    const amt = Number(stakeAmount);
    if (amt <= 0 || amt > (userData?.userMoney || 0)) return;

    setLoading(true);
    try {
      const token = await currentUser.getIdToken(false);
      const res = await fetch("https://stakefunds-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify({ amount: amt, days: duration })
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Staking failed.");
      }
      setStakeAmount("");
    } catch (err) {
      console.error("Staking error:", err.message);
      alert(`Protocol Rejected: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    setLoading(true);
    try {
      const token = await currentUser.getIdToken(false);
      const res = await fetch("https://claimmaturedstake-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "Authorization": `Bearer ${token}` 
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Claim failed.");
      }
    } catch (err) {
      console.error("Claim error:", err.message);
      alert(`Withdrawal Denied: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isOverBalance = Number(stakeAmount) > (userData?.userMoney || 0);

if (activeStake) {
  return (
    <div style={{ width: '100%', fontFamily: 'JetBrains Mono, monospace' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h3 style={{ color: THEME_TEAL, margin: 0, fontSize: '1rem', letterSpacing: '2px' }}>ACTIVE COMMITMENT</h3>
          <div style={{ fontSize: '0.8rem', color: isMatured ? THEME_TEAL : '#555', marginTop: '2px' }}>
            {isMatured ? "TERM COMPLETE" : "FUNDS LOCKED"}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.55rem', color: TEXT_GREY }}>STATUS</div>
          <div style={{ fontSize: '0.8rem', color: isMatured ? THEME_TEAL : '#fff' }}>
            {isMatured ? "READY TO CLAIM" : "WORKING"}
          </div>
        </div>
      </div>

      <div style={{ background: '#050505', border: '1px solid #1a1a1a', padding: '20px', marginBottom: '20px' }}>
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontSize: '0.55rem', color: TEXT_GREY, marginBottom: '4px' }}>CAPITAL</div>
          <div style={{ fontSize: '1.4rem', color: '#fff' }}>${activeStake.amount?.toLocaleString()}</div>
        </div>

        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.55rem', color: TEXT_GREY, marginBottom: '4px' }}>EST. TOTAL YIELD</div>
            <div style={{ fontSize: '1rem', color: THEME_TEAL }}>+{activeStake.projectedTotal?.toFixed(2)} VP</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.55rem', color: TEXT_GREY, marginBottom: '4px' }}>TERM DURATION</div>
            <div style={{ fontSize: '1rem', color: '#fff' }}>{activeStake.days} DAYS</div>
          </div>
        </div>
      </div>

      <div style={{ background: 'rgba(80, 227, 194, 0.03)', border: '1px dashed #222', padding: '15px', textAlign: 'center' }}>
        <div style={{ fontSize: '0.6rem', color: TEXT_GREY, marginBottom: '5px' }}>MATURATION DATE</div>
        <div style={{ fontSize: '0.8rem', color: '#fff', letterSpacing: '1px' }}>
          {activeStake.endDate?.seconds 
            ? new Date(activeStake.endDate.seconds * 1000).toLocaleDateString() 
            : "LOADING..."}
        </div>
      </div>

      {isMatured && (
        <button
          className="jetbrains-btn"
          onClick={handleClaim}
          disabled={loading}
          style={{ 
            marginTop: '20px', width: '100%', padding: '14px', fontWeight: 'bold', 
            background: THEME_TEAL, color: '#000', cursor: 'pointer', border: 'none'
          }}
        >
          {loading ? "CLAIMING..." : "CLAIM FUNDS & VP"}
        </button>
      )}

      {/* Notice displayed regardless of maturation status */}
      <div style={{ marginTop: '25px', fontSize: '0.7rem', color: '#50E3C2', textAlign: 'center', lineHeight: '1.4' }}>
        NOTICE: FUNDS ARE LOCKED UNTIL THE TERM CONCLUDES.
        <br />EARNINGS ARE DISPERSED UPON MATURATION.
      </div>
    </div>
  );
}

return (
  <div style={{ width: '100%', fontFamily: 'JetBrains Mono, monospace' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '25px' }}>
      <div>
        <h3 style={{ color: THEME_TEAL, margin: 0, fontSize: '1rem', letterSpacing: '2px' }}>POLITICAL STAKE</h3>
        <div style={{ fontSize: '0.6rem', color: '#555', marginTop: '2px' }}>{tiers[duration].desc}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: '0.55rem', color: TEXT_GREY }}>WALLET</div>
        <div style={{ fontSize: '0.9rem', color: THEME_TEAL }}>${userData?.userMoney?.toLocaleString() || "0"}</div>
      </div>
    </div>

    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
      {[7, 14, 30, 90].map((d) => {
        const isSelected = duration === d;
        return (
          <button
            key={d}
            style={{
              flex: 1, fontSize: '0.7rem', padding: '10px 0', cursor: 'pointer',
              border: `1px solid ${isSelected ? THEME_TEAL : DEFAULT_GREY}`,
              color: isSelected ? THEME_TEAL : '#444',
              background: isSelected ? `${THEME_TEAL}10` : 'transparent',
              outline: 'none'
            }}
            onClick={() => setDuration(d)}
          >
            {tiers[d].label}
            <div style={{ fontSize: '0.5rem', opacity: isSelected ? 1 : 0.5 }}>{tiers[d].mult.toFixed(1)}x</div>
          </button>
        );
      })}
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label style={{ fontSize: '0.6rem', color: TEXT_GREY, display: 'block', marginBottom: '6px' }}>COMMITMENT AMOUNT</label>
      <input
        type="number"
        placeholder="0.00"
        value={stakeAmount}
        onChange={(e) => setStakeAmount(e.target.value)}
        style={{ 
          width: '100%', background: '#000', padding: '12px', fontSize: '1.2rem', textAlign: 'right',
          border: `1px solid ${isOverBalance ? '#f44336' : '#222'}`,
          color: isOverBalance ? '#f44336' : THEME_TEAL, boxSizing: 'border-box',
          outline: 'none', fontFamily: 'inherit'
        }}
      />
    </div>

    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '15px', border: '1px solid #1a1a1a', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '0.65rem', color: TEXT_GREY }}>DAILY ACCRUAL RATE</span>
        <span style={{ fontSize: '0.75rem', color: THEME_TEAL }}>+{projectedEarnings.daily.toFixed(4)} VP</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #1a1a1a', paddingTop: '10px' }}>
        <span style={{ fontSize: '0.65rem', color: TEXT_GREY }}>TOTAL PROJECTED YIELD</span>
        <span style={{ fontSize: '0.85rem', color: THEME_TEAL, fontWeight: 'bold' }}>{projectedEarnings.total.toFixed(4)} VP</span>
      </div>
    </div>

    <button
      className="jetbrains-btn"
      disabled={loading || !stakeAmount || isOverBalance || Number(stakeAmount) <= 0}
      onClick={handleStake}
      style={{ 
        width: '100%', padding: '14px', fontWeight: 'bold', letterSpacing: '2px', fontSize: '0.75rem',
        background: loading || isOverBalance ? 'transparent' : `${THEME_TEAL}15`,
        borderColor: isOverBalance ? '#333' : THEME_TEAL,
        color: isOverBalance ? '#333' : THEME_TEAL,
        cursor: 'pointer'
      }}
    >
      {loading ? "PROCESSING..." : "STAKE FUNDS"}
    </button>

    {/* Notice displayed on the staking setup screen */}
    <div style={{ marginTop: '25px', fontSize: '0.7rem', color: '#50E3C2', textAlign: 'center', lineHeight: '1.4' }}>
      NOTICE: FUNDS ARE LOCKED UNTIL THE TERM CONCLUDES.
      <br />EARNINGS ARE DISPERSED UPON MATURATION.
    </div>
  </div>
);
};

export default PoliticalStaking;